import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inputCls, labelCls } from "@/components/common/states";
import { Plus, Star, Eye, EyeOff, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", description: "", image: "", featured: false, active: true, seo_title: "", seo_description: "" };

export default function AdminLocations() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  useSEO({ title: "Locations · Admin" });

  const load = () => apiGet("/locations").then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggle = async (l, f) => { await apiPut(`/admin/locations/${l.slug}`, { [f]: !l[f] }); toast.success("Updated"); load(); };
  const remove = async (l) => { if (!window.confirm(`Delete "${l.name}"?`)) return; await apiDelete(`/admin/locations/${l.slug}`); toast.success("Deleted"); load(); };
  const set = (k) => (ev) => setEditing((s) => ({ ...s, [k]: ev.target.type === "checkbox" ? ev.target.checked : ev.target.value }));

  const save = async () => {
    const e = editing;
    if (!e.name.trim()) return toast.error("Name is required");
    const isNew = !items.find((it) => it.slug === e.slug);
    const res = isNew ? await apiPost("/admin/locations", e) : await apiPut(`/admin/locations/${e.slug}`, e);
    if (res.success) { toast.success("Saved"); setEditing(null); load(); } else toast.error("Save failed");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-foreground">Locations</h1><p className="text-muted-foreground">{items?.length ?? 0} areas</p></div>
        <Button data-testid="add-location" onClick={() => setEditing({ ...BLANK })} className="bg-primary text-primary-foreground gap-2"><Plus className="h-4 w-4" aria-hidden /> Add Location</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items?.map((l) => (
          <div key={l.slug} className="rounded-lg border border-border bg-white overflow-hidden">
            <div className="relative h-32"><img src={l.image} alt={l.name} className="h-full w-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-1.5">
                <button onClick={() => toggle(l, "featured")} className={`h-8 w-8 grid place-items-center rounded-sm ${l.featured ? "bg-accent text-accent-foreground" : "bg-white/90 text-muted-foreground"}`}><Star className="h-4 w-4" aria-hidden /></button>
                <button onClick={() => toggle(l, "active")} className={`h-8 w-8 grid place-items-center rounded-sm ${l.active ? "bg-green-500 text-white" : "bg-white/90 text-muted-foreground"}`}>{l.active ? <Eye className="h-4 w-4" aria-hidden /> : <EyeOff className="h-4 w-4" aria-hidden />}</button>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg text-foreground">{l.name}</h3>
                <div className="flex gap-1.5">
                  <button onClick={() => setEditing({ ...l })} className="h-8 w-8 grid place-items-center rounded-sm border border-border text-primary hover:bg-muted"><Pencil className="h-4 w-4" aria-hidden /></button>
                  <button onClick={() => remove(l)} className="h-8 w-8 grid place-items-center rounded-sm border border-border text-destructive hover:bg-destructive/5"><Trash2 className="h-4 w-4" aria-hidden /></button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.description}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          {editing && (<>
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing.slug ? "Edit" : "New"} Location</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className={labelCls}>Name *</label><input value={editing.name} onChange={set("name")} className={inputCls} /></div>
              <div><label className={labelCls}>Image URL</label><input value={editing.image} onChange={set("image")} className={inputCls} /></div>
              <div><label className={labelCls}>Description</label><textarea value={editing.description} onChange={set("description")} rows={3} className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" /></div>
              <div><label className={labelCls}>SEO Title</label><input value={editing.seo_title || ""} onChange={set("seo_title")} className={inputCls} /></div>
              <div><label className={labelCls}>SEO Description</label><input value={editing.seo_description || ""} onChange={set("seo_description")} className={inputCls} /></div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={set("featured")} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active !== false} onChange={set("active")} /> Published</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4"><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} className="bg-primary text-primary-foreground">Save</Button></div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
